import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { Badge, BadgeInput, ListBadgesResponse } from 'shared';

export type { Badge, BadgeInput };

// ─── Query key factory ────────────────────────────────────────────────────────

export const badgeKeys = {
  all: ['badges'] as const,
  list: (params: ListParams) => [...badgeKeys.all, 'list', params] as const,
  detail: (id: string) => [...badgeKeys.all, 'detail', id] as const,
};

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ListParams {
  limit?: number;
  offset?: number;
  search?: string;
  activeOnly?: boolean;
  showDeleted?: boolean;
}

// ─── Hooks ────────────────────────────────────────────────────────────────────

/** Paginated, searchable and filterable list of badge definitions. */
export function useBadges(params: ListParams = {}) {
  const {
    limit = 20,
    offset = 0,
    search = '',
    activeOnly = false,
    showDeleted = false,
  } = params;

  const qs = new URLSearchParams({
    limit: String(limit),
    offset: String(offset),
    ...(search ? { search } : {}),
    ...(activeOnly ? { activeOnly: 'true' } : {}),
    ...(showDeleted ? { showDeleted: 'true' } : {}),
  });

  return useQuery({
    queryKey: badgeKeys.list(params),
    queryFn: () => api.get<ListBadgesResponse>(`/api/badges?${qs}`),
    staleTime: 1000 * 30,
    placeholderData: (prev) => prev,
  });
}

/** Single badge by UUID. */
export function useBadge(id: string | null, showDeleted = false) {
  return useQuery({
    queryKey: badgeKeys.detail(id!),
    queryFn: () =>
      api.get<Badge>(`/api/badges/${id}${showDeleted ? '?showDeleted=true' : ''}`),
    enabled: id !== null,
  });
}

/** Create a new badge. */
export function useCreateBadge() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: BadgeInput) => api.post<Badge>('/api/badges', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: badgeKeys.all }),
  });
}

/** Update an existing badge. */
export function useUpdateBadge() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: BadgeInput }) =>
      api.put<Badge>(`/api/badges/${id}`, data),
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: badgeKeys.all });
      qc.invalidateQueries({ queryKey: badgeKeys.detail(id) });
    },
  });
}

/** Soft-delete a badge (requires a reason). Rejects with ApiConflictError if in use. */
export function useDeleteBadge() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      api.delete<void>(`/api/badges/${id}`, { reason }),
    onSuccess: () => qc.invalidateQueries({ queryKey: badgeKeys.all }),
  });
}

/** All active (non-deleted, active=true) badges for use in dropdowns. */
export function useBadgesDropdown() {
  return useQuery({
    queryKey: ['badges', 'dropdown'],
    queryFn: () =>
      api.get<ListBadgesResponse>('/api/badges?limit=200&offset=0&activeOnly=true'),
    staleTime: 1000 * 60 * 5,
    select: (data) => data.badges,
  });
}
